"use client";

import { IconPlusOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import { CheckBox } from "@/components/ui/CheckBox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { jsonSchemaToWorkflowInputs, workflowInputsToJsonSchema } from "@/lib/workflow/inputSchema";
import * as React from "react";
export interface SchemaField {
  id: string; // Unique ID for React key
  name: string;
  type: "string" | "number" | "boolean" | "object" | "array";
  description: string;
  required: boolean;
}

// Generate unique ID for fields
const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
interface InputSchemaBuilderProps {
  initialFields: SchemaField[];
  onChange: (fields: SchemaField[]) => void;
}
const FIELD_TYPES = [{
  value: "string",
  label: "Text"
}, {
  value: "number",
  label: "Number"
}, {
  value: "boolean",
  label: "Boolean"
}, {
  value: "object",
  label: "Object"
}, {
  value: "array",
  label: "Array"
}] as const;
function sanitizeSchemaFieldName(value: string): string {
  return value.replace(/[^a-zA-Z0-9_]/g, "_");
}
function InputSchemaBuilder({
  initialFields,
  onChange
}: InputSchemaBuilderProps) {
  // Manage fields internally to avoid round-trip conversion issues
  const [fields, setFields] = React.useState<SchemaField[]>(() => initialFields);
  const serializeFields = React.useCallback((nextFields: SchemaField[]) => JSON.stringify(nextFields.map(({
    name,
    type,
    description,
    required
  }) => ({
    name,
    type,
    description,
    required
  }))), []);
  const previousInitialFieldsRef = React.useRef(serializeFields(initialFields));
  React.useEffect(() => {
    const nextInitialSerialized = serializeFields(initialFields);
    if (previousInitialFieldsRef.current === nextInitialSerialized) return;
    previousInitialFieldsRef.current = nextInitialSerialized;
    setFields(initialFields);
  }, [initialFields, serializeFields]);

  // Notify parent when fields change - but debounce to avoid render loops
  const notifyParentRef = React.useRef(onChange);
  notifyParentRef.current = onChange;
  const notifyParent = React.useCallback((newFields: SchemaField[]) => {
    // Only notify if there are valid fields (with names)
    notifyParentRef.current(newFields);
  }, []);
  const addField = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newFields_0 = [...fields, {
      id: generateFieldId(),
      name: "",
      type: "string" as const,
      description: "",
      required: false
    }];
    setFields(newFields_0);
    // Don't notify parent yet - wait until field has a name
  };
  const updateField = (index: number, updates: Partial<SchemaField>) => {
    const newFields_1 = [...fields];
    newFields_1[index] = {
      ...newFields_1[index],
      ...updates
    };
    setFields(newFields_1);
    // Only notify parent if the field has a name (or if we're updating something other than name)
    // This prevents the field from being removed while typing
    if (newFields_1[index].name || !("name" in updates)) {
      notifyParent(newFields_1);
    }
  };
  const removeField = (e_0: React.MouseEvent, index_0: number) => {
    e_0.preventDefault();
    e_0.stopPropagation();
    const newFields_2 = fields.filter((_, i) => i !== index_0);
    setFields(newFields_2);
    notifyParent(newFields_2);
  };
  return <div className="space-y-3">
      {fields.length === 0 ? <div className="border-ds-neutral-300 dark:border-ds-neutral-700 space-y-4 rounded-lg border border-dashed p-6 text-center">
          <div className="space-y-2">
            <p className="label label-xs">No input fields defined</p>
            <p className="caption-xs">{`Click "Add Field" to define expected input data.`}</p>
          </div>
          <div className="flex justify-center">
            <Button onClick={addField} variant="white" size="sm" className="gap-2 px-4 py-2 text-xs">
              <IconPlusOutline18 className="h-3 w-3" />
              Add Field
            </Button>
          </div>
        </div> : <div className="space-y-3">
          <div className="space-y-2">
            {fields.map((field, index_1) => <div key={field.id} className="border-ds-border-default bg-ds-surface-muted space-y-3 rounded-xl border p-4">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(112px,140px)] items-center gap-3">
                  {/* Field Name */}
                  <div className="flex-1">
                    <Input type="text" value={field.name} onChange={e_1 => updateField(index_1, {
                name: sanitizeSchemaFieldName(e_1.target.value)
              })} placeholder="field_name" className="text-ds-text-primary dark:bg-ds-neutral-800 h-11 px-3 font-mono dark:border-zinc-600" />
                  </div>

                  {/* Field Type */}
                  <Select value={field.type} onValueChange={v => updateField(index_1, {
              type: v as SchemaField["type"]
            })}>
                    <SelectTrigger className="border-ds-neutral-300 text-ds-text-primary bg-ds-surface-card h-11 w-full rounded-lg border text-xs font-medium dark:border-zinc-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="items-center py-2 text-xs font-medium">
                          {t.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description */}
                <Input type="text" value={field.description} onChange={e_2 => updateField(index_1, {
            description: e_2.target.value
          })} placeholder="Description (shown to payers)" className="text-ds-text-primary dark:bg-ds-neutral-800 h-11 px-3 dark:border-zinc-600" />

                <div className="border-ds-neutral-200/80 dark:border-ds-neutral-700/80 flex items-center justify-between gap-3 border-t pt-1">
                  <div className="flex items-center gap-2">
                    <CheckBox checked={field.required} onChange={checked => updateField(index_1, {
                required: checked
              })} size="sm" className="items-center gap-0" />
                    <span className="text-ds-text-secondary text-sm font-medium">Required</span>
                  </div>

                  <button type="button" onClick={e_3 => removeField(e_3, index_1)} className="text-ds-text-disabled hover:bg-ds-neutral-200 dark:hover:bg-ds-neutral-700 flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:text-red-600">
                    <IconTrashOutline18 className="h-4 w-4" />
                  </button>
                </div>
              </div>)}
          </div>

          <div className="flex justify-center">
            <Button onClick={addField} variant="white" size="sm" className="gap-2 px-4 py-2 text-xs">
              <IconPlusOutline18 className="h-3 w-3" />
              Add Field
            </Button>
          </div>
        </div>}
    </div>;
}

// Convert SchemaField[] to JSON Schema format
export function fieldsToJsonSchema(fields: SchemaField[]): Record<string, unknown> | undefined {
  return workflowInputsToJsonSchema(fields.filter(field => field.name).map(field => ({
    key: field.name,
    type: field.type,
    description: field.description || undefined,
    required: field.required
  })));
}

// Convert JSON Schema back to SchemaField[]
export function jsonSchemaToFields(schema: Record<string, unknown> | undefined): SchemaField[] {
  return jsonSchemaToWorkflowInputs(schema).map(field => ({
    id: generateFieldId(),
    name: field.key,
    type: field.type,
    description: field.description || "",
    required: field.required
  }));
}
export default React.memo(InputSchemaBuilder);